import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CLINIC, services as fallbackServices, team as fallbackTeam } from "@/lib/clinic";
import type { Lang } from "@/contexts/LanguageContext";

// ============ SITE SETTINGS ============
export type SiteSettings = typeof CLINIC & Record<string, string>;

async function fetchSettings(): Promise<Record<string, string>> {
  const { data } = await supabase.from("site_settings").select("key,value");
  const map: Record<string, string> = {};
  (data ?? []).forEach((r: { key: string; value: string | null }) => {
    if (r.value != null) map[r.key] = r.value;
  });
  return map;
}

export function useSiteSettings() {
  const { data } = useQuery({
    queryKey: ["site_settings"],
    queryFn: fetchSettings,
    staleTime: 60_000,
  });
  const merged: SiteSettings = {
    ...CLINIC,
    name: data?.clinic_name ?? CLINIC.name,
    nameHe: data?.clinic_name_he ?? CLINIC.nameHe,
    tagline: data?.tagline ?? CLINIC.tagline,
    phone: data?.phone ?? CLINIC.phone,
    mobile: data?.mobile ?? CLINIC.mobile,
    whatsapp: data?.whatsapp ?? CLINIC.whatsapp,
    email: data?.email ?? CLINIC.email,
    address: data?.address ?? CLINIC.address,
    shopUrl: data?.shop_url ?? CLINIC.shopUrl,
    externalSite: data?.external_site ?? CLINIC.externalSite,
    facebook: data?.facebook ?? CLINIC.facebook,
    instagram: data?.instagram ?? CLINIC.instagram,
    waze: data?.waze ?? CLINIC.waze,
    ...(data ?? {}),
  } as SiteSettings;
  return merged;
}

// ============ PAGE CONTENT ============
type PageContentRow = {
  key: string;
  title_he: string | null;
  title_ar: string | null;
  body_he: string | null;
  body_ar: string | null;
};

export function usePageContent() {
  const { data } = useQuery({
    queryKey: ["page_content"],
    queryFn: async (): Promise<Record<string, PageContentRow>> => {
      const { data } = await supabase.from("page_content").select("*");
      const map: Record<string, PageContentRow> = {};
      (data ?? []).forEach((r) => {
        map[r.key] = r as PageContentRow;
      });
      return map;
    },
    staleTime: 60_000,
  });
  return (key: string, lang: Lang, field: "title" | "body" = "body", fallback = "") => {
    const row = data?.[key];
    if (!row) return fallback;
    const v = row[`${field}_${lang}` as keyof PageContentRow] as string | null | undefined;
    return v || fallback;
  };
}

// ============ SERVICES ============
export type ServiceRow = {
  id: string;
  slug: string;
  title_he: string;
  title_ar: string;
  short_he: string | null;
  short_ar: string | null;
  description_he: string | null;
  description_ar: string | null;
  icon: string;
  display_order: number;
  is_active: boolean;
};

export function useServices() {
  return useQuery({
    queryKey: ["services"],
    queryFn: async (): Promise<ServiceRow[]> => {
      const { data } = await supabase
        .from("services")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      if (data && data.length > 0) return data as ServiceRow[];
      // fallback to hardcoded
      return fallbackServices.map((s, i) => ({
        id: s.slug,
        slug: s.slug,
        title_he: s.title,
        title_ar: s.title,
        short_he: s.short,
        short_ar: s.short,
        description_he: s.description,
        description_ar: s.description,
        icon: s.icon,
        display_order: i,
        is_active: true,
      }));
    },
    staleTime: 60_000,
  });
}

// ============ TEAM ============
export type TeamRow = {
  id: string;
  name_he: string;
  name_ar: string;
  role_he: string | null;
  role_ar: string | null;
  initials: string | null;
  image_url: string | null;
  display_order: number;
  is_active: boolean;
  slug?: string | null;
  has_profile_page?: boolean;
  name_en?: string | null;
  tags?: string[] | null;
  image_focus_x?: number | null;
  image_focus_y?: number | null;
};

export function useTeam() {
  return useQuery({
    queryKey: ["team_members"],
    queryFn: async (): Promise<TeamRow[]> => {
      const { data } = await supabase
        .from("team_members")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      if (data && data.length > 0) return data as TeamRow[];
      return fallbackTeam.map((m, i) => ({
        id: String(i),
        name_he: m.name,
        name_ar: m.name,
        role_he: m.role,
        role_ar: m.role,
        initials: m.initials,
        image_url: m.image,
        display_order: i,
        is_active: true,
        slug: null,
        has_profile_page: false,
      }));
    },
    staleTime: 60_000,
  });
}
